import React, { useEffect } from 'react';
import { css } from 'emotion';
import { RetinaPicture } from 'components/RetinaPicture';
import { useT } from 'client/i18n/hooks/useT';
import {
  TextLink,
  ButtonPrimary,
  screenSmMax,
} from '@spotify-internal/encore-web';
import { Modal } from '@spotify-internal/anchor-ds';
import { paths } from 'client/shared/Paths';
import { events } from './events';

const SpotifyOnlyDistributionModal = ({
  onClickClose,
  onOptInClick,
  isLoading,
  isError,
}: {
  onClickClose: () => void;
  onOptInClick: () => void;
  isLoading: boolean;
  isError: boolean;
}) => {
  const t = useT();
  // analytics
  useEffect(() => {
    events.distributeToSpotifyModalScreenView();
  }, []);

  return (
    <Modal
      isShowing
      isShowingCloseButton
      aria-labelledby="spotify-only-distro-title"
      onClose={() => {
        events.distributeToSpotifyExitButtonClicked();
        onClickClose();
      }}
      size="small"
    >
      <Modal.Title id="spotify-only-distro-title">
        {t(
          'PreDistributionModalScreen.BinaryDistributionPrompt.SpotifyOnlyDistributionModal.Title',
          'Ready to publish?',
          'Distribute only to Spotify modal title'
        )}
      </Modal.Title>
      <Modal.Subtitle>
        {t(
          'PreDistributionModalScreen.BinaryDistributionPrompt.SpotifyOnlyDistributionModal.Message',
          'This will make your podcast available on Anchor and Spotify. You can distribute your podcast to more platforms in the next step.',
          'Distribute only to Spotify modal message'
        )}
      </Modal.Subtitle>
      <Modal.Body>
        <RetinaPicture
          className={css`
            width: 328px;
            height: 360px;

            @media (max-width: ${screenSmMax}) {
              width: 228px;
              height: 260px;
            }
          `}
          imagePath="/distribution/ic_distribution_preview"
          alt={t(
            'PreDistributionModalScreen.BinaryDistributionPrompt.ImgAlt',
            "Minimalized display of Spotify's mobile user interface",
            'Enable RSS feed confirmation modal image description'
          )}
          fallbackExtension="png"
        />
      </Modal.Body>
      {isError && (
        <Modal.Error>
          {t(
            'PreDistributionModalScreen.BinaryDistributionPrompt.ErrorMessage',
            'Hm, something went wrong. Please try again or if the issue persists, reach out to us at {helpPageLink}.',
            'Distribute to Spotify modal error message',
            {
              helpPageLink: (
                <TextLink
                  key="help-link"
                  href={paths.getHelpPage('home')}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {paths.getHelpPage('home')}
                </TextLink>
              ),
            }
          )}
        </Modal.Error>
      )}
      <Modal.Footer>
        <ButtonPrimary
          onClick={() => {
            events.distributeToSpotifyPublishButtonClicked();
            onOptInClick();
          }}
          disabled={isLoading}
        >
          {isError
            ? t('Button.Retry', 'Retry', 'Retry button text')
            : t('Button.Publish', 'Publish', 'Publish button text')}
        </ButtonPrimary>
      </Modal.Footer>
    </Modal>
  );
};

export { SpotifyOnlyDistributionModal };
